import { useEffect, useMemo, useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { Eye, MousePointerClick, BarChart3, Loader2, AlertCircle, RefreshCw, Link2, Megaphone } from 'lucide-preact'
import { websApi } from '@/api/webs'
import { useAnalyticsView } from '@/hooks/useAnalyticsView'
import { useCountUp } from '@/hooks/useCountUp'
import type { AnalyticsMetricDto, AnalyticsViewDto, AttributionView } from '@/types/analytics'
import { formatMetricValue, type MetricFormat } from '@/utils/analyticsFormat'
import { AttributionViewSelect } from '@/components/dashboard/analytics/AttributionViewSelect'
import { AnalyticsExportButton } from '@/components/dashboard/analytics/AnalyticsExportButton'
import { TopAttributionList } from '@/components/dashboard/analytics/TopAttributionList'
import { ScrollDepthFunnelTile } from '@/components/dashboard/analytics/ScrollDepthFunnelTile'

interface Props {
    workspaceId: string
    webId: string
    /**
     * External-mode landing pages are hosted off-platform, so conversions are not
     * measured here. Render "—" for the Conversions row to signal "not measured"
     * rather than "measured zero" (ORB-328 Q5). The richer M1 rows (visitor breakdown,
     * referrers, UTM) are also suppressed in external mode for the same reason.
     */
    externalMode?: boolean
    /** Whether to show the CSV export button — gated on the export permission by the host. */
    canExport?: boolean
}

/** Read a metric's current value as display text, or "—" when the metric is absent. */
function metricText(metric: AnalyticsMetricDto | undefined, format: MetricFormat = 'count'): string {
    if (!metric) return '—'
    return formatMetricValue(metric.Current, format)
}

/** A small label/value pair for the range-less visitor breakdown grid. */
function CompactStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0">
            <div className="text-2xs uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate">{label}</div>
            <div className="mt-0.5 text-base font-bold text-slate-900 dark:text-white tabular-nums">{value}</div>
        </div>
    )
}

/** Percentage of `n` relative to `d` (0 when `d` is empty). */
function pctOf(n: number, d: number): number {
    if (d <= 0) return 0
    return (n / d) * 100
}

/**
 * Card chrome (border + a floating refresh button) shared across the loading / error /
 * data states. Fills its grid cell height (flex column) so the funnel can distribute to
 * match the neighbouring status card; the refresh button floats top-right (no header row).
 */
function AnalyticsCardShell({ onRefresh, refreshing, children }: { onRefresh: () => void; refreshing: boolean; children: ComponentChildren }) {
    return (
        <div className="relative flex flex-col h-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-6">
            <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                title="Refresh analytics"
                aria-label="Refresh analytics"
                className="absolute top-4 right-4 inline-flex items-center justify-center rounded-md p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {children}
        </div>
    )
}

/** A thin proportion bar that animates its width from 0 to `pct` on mount / value change. */
function FunnelBar({ pct, gradient }: { pct: number; gradient: string }) {
    const [width, setWidth] = useState(0)
    useEffect(() => {
        const id = requestAnimationFrame(() => setWidth(Math.max(0, Math.min(100, pct))))
        return () => cancelAnimationFrame(id)
    }, [pct])
    return (
        <div className="h-1.5 flex-1 rounded-full bg-slate-100 dark:bg-slate-700/60 overflow-hidden">
            <div
                className={`h-full rounded-full bg-gradient-to-r ${gradient} transition-[width] duration-700 ease-out`}
                style={{ width: `${width}%` }}
            />
        </div>
    )
}

interface FunnelMetricProps {
    icon: typeof Eye
    label: string
    value: string
    gradient: string
    barPct: number
    barLabel?: string
    badge?: string
}

/** One funnel stage: gradient icon chip, big value, optional CVR badge, and an animated proportion bar. */
function FunnelMetric({ icon: Icon, label, value, gradient, barPct, barLabel, badge }: FunnelMetricProps) {
    return (
        <div>
            <div className="flex items-center gap-2.5">
                <div className={`flex-shrink-0 p-1.5 rounded-md bg-gradient-to-br ${gradient} text-white shadow-sm`}>
                    <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate">{label}</p>
                        {badge && (
                            <span className="flex-shrink-0 inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 text-2xs font-semibold text-emerald-700 dark:text-emerald-400">
                                {badge}
                            </span>
                        )}
                    </div>
                    <p className="text-base font-bold text-slate-900 dark:text-white leading-tight tabular-nums">{value}</p>
                </div>
            </div>
            {barLabel && (
                <div className="mt-1.5 flex items-center gap-2 pl-9">
                    <FunnelBar pct={barPct} gradient={gradient} />
                    <span className="flex-shrink-0 w-20 text-right text-2xs font-medium text-slate-400 dark:text-slate-500 tabular-nums">{barLabel}</span>
                </div>
            )}
        </div>
    )
}

/**
 * Attribution-view selector + permission-gated CSV export. Both reflect the Visitors tab's
 * scope — attribution credits sessions/conversions to sources, and the export downloads the
 * web's visitor/session data under the active view — so this row lives on that tab.
 */
function AnalyticsControls({ workspaceId, webId, attributionView, onAttributionChange, canExport }: {
    workspaceId: string
    webId: string
    attributionView: AttributionView
    onAttributionChange: (view: AttributionView) => void
    canExport: boolean
}) {
    return (
        <div className="flex items-center justify-end gap-3">
            <AttributionViewSelect value={attributionView} onChange={onAttributionChange} id={`web-attribution-${webId}`} />
            {canExport && (
                <AnalyticsExportButton
                    onExport={() => websApi.exportAnalytics(workspaceId, webId, false, attributionView)}
                    fallbackFilename={`visitor-export-web-${webId}-alltime-${attributionView.toLowerCase()}.csv`}
                />
            )}
        </div>
    )
}

/**
 * The three-stage visitor funnel (Total views → Unique views → Conversions) with count-up
 * values and step-through bars. Range-less; external-mode conversions render "—".
 *
 * ORB-534: the middle stage was Thank-you page views, which tracks Conversions by construction —
 * the thank-you page is where a conversion lands — so on a send that converted nobody the funnel
 * showed 0 twice under two labels and the stage carried no information. Unique views (distinct
 * sessions) does: it separates reach from repeat visits, which is what the reader wants there.
 *
 * ORB-625 removed the Thank-you fallback this once carried for deploys where the API had not yet
 * shipped UniqueLandingPageViews. Thank-you views are no longer rendered anywhere, so falling back
 * to them would put a different measure under the unique-views label — a silent substitution, where
 * an unshipped field should read as the visible gap it is.
 */
function LandingPageFunnel({ data, externalMode }: { data: AnalyticsViewDto | null; externalMode: boolean }) {
    const landingPageViews = data?.LandingPageViews.Current ?? 0
    const uniqueViewsMetric = data?.UniqueLandingPageViews
    const hasUniqueViews = uniqueViewsMetric != null
    const middleValue = uniqueViewsMetric?.Current ?? 0
    const conversions = data?.Conversions.Current ?? 0
    const landingPageViewsAnim = useCountUp(landingPageViews)
    const middleValueAnim = useCountUp(middleValue)
    const conversionsAnim = useCountUp(conversions)

    const middleShare = Math.round(pctOf(middleValue, landingPageViews))
    // Conversions step off the middle stage, whichever it currently is. The denominator is named in
    // short form because FunnelMetric renders barLabel in a fixed w-20 (80px) non-truncating span —
    // "100% of unique views" overflows it, and widening the column is the layout change this ticket
    // was told not to make. The stage it refers to is labelled in full directly above.
    const convStep = Math.round(pctOf(conversions, middleValue))
    // External-mode conversions are not measured here — render "—" and drop the bar/badge.
    const convValue = externalMode ? '—' : conversionsAnim.toLocaleString()
    // No unique-views number means no denominator, so the two derived percentages are not 0 — they
    // are unknown, and printing 0% beside a "—" asserts something the payload did not say.
    const convBarLabel = externalMode || !hasUniqueViews ? undefined : `${convStep}% of unique`
    const convBadge = externalMode ? undefined : `${pctOf(conversions, landingPageViews).toFixed(1)}% CVR`

    return (
        <div className="flex flex-col gap-3">
            <FunnelMetric
                icon={Eye}
                label="Total views"
                value={landingPageViewsAnim.toLocaleString()}
                gradient="from-sky-400 to-sky-500"
                barPct={100}
                barLabel="100%"
            />
            <FunnelMetric
                icon={MousePointerClick}
                label="Unique views"
                value={hasUniqueViews ? middleValueAnim.toLocaleString() : '—'}
                gradient="from-violet-400 to-violet-500"
                barPct={pctOf(middleValue, landingPageViews)}
                barLabel={hasUniqueViews ? `${middleShare}% of views` : undefined}
            />
            <FunnelMetric
                icon={BarChart3}
                label="Conversions"
                value={convValue}
                gradient="from-emerald-400 to-emerald-500"
                barPct={pctOf(conversions, middleValue)}
                barLabel={convBarLabel}
                badge={convBadge}
            />
        </div>
    )
}

/** Range-less M1 visitor breakdown grid + top referrers / UTM campaigns (measured pages only). */
function VisitorBreakdownSection({ data }: { data: AnalyticsViewDto | null }) {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
                <CompactStat label="Unique" value={metricText(data?.UniqueVisitors)} />
                <CompactStat label="Known" value={metricText(data?.UniqueKnownVisitors)} />
                <CompactStat label="Unknown" value={metricText(data?.UniqueUnknownVisitors)} />
                <CompactStat label="Returning" value={metricText(data?.ReturningVisitors)} />
                <CompactStat label="Sessions" value={metricText(data?.Sessions)} />
                <CompactStat label="Conv. rate" value={metricText(data?.ConversionRate, 'percent')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <TopAttributionList
                    bare
                    title="Top Referrers"
                    icon={<Link2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />}
                    items={data?.TopReferrers}
                    emptyMessage="No referrer data yet."
                />
                <TopAttributionList
                    bare
                    title="Top UTM Campaigns"
                    icon={<Megaphone className="w-4 h-4 text-primary-600 dark:text-primary-400" />}
                    items={data?.TopUtmCampaigns}
                    emptyMessage="No campaign data yet."
                />
            </div>
        </div>
    )
}

/**
 * Range-less engagement breakdown (ORB-374 · M2): dwell/scroll/interaction headline stats plus
 * the scroll-depth milestone funnel. Mirrors the visitor breakdown layout (compact stat grid +
 * a bare list) so the two read consistently within the card (measured pages only).
 */
function EngagementBreakdownSection({ data }: { data: AnalyticsViewDto | null }) {
    const engagement = data?.Engagement
    // ORB-534: the max-scroll average is now taken over sessions that actually scrolled, so it needs
    // its population visible. An average with a hidden denominator is how the old 3.8% went
    // unquestioned, and 90% over two sessions would be the same mistake pointed the other way.
    //
    // "scrolled session(s)" rather than plain "session(s)": the count is the scrolled subset, not the
    // total, and dropping the qualifier to fix the grammar would trade one ambiguity for a worse one.
    const scrolledSessions = engagement?.ScrolledSessions
    const scrollLabel =
        scrolledSessions == null
            ? 'Max scroll'
            : `Max scroll · ${scrolledSessions} scrolled ${scrolledSessions === 1 ? 'session' : 'sessions'}`
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-x-4 gap-y-2.5">
                <CompactStat label="Time on page" value={metricText(engagement?.AvgTimeOnPageSeconds, 'duration')} />
                <CompactStat label={scrollLabel} value={metricText(engagement?.AvgMaxScrollPct, 'percent')} />
                <CompactStat label="CTA clicks" value={metricText(engagement?.CtaClicks)} />
                <CompactStat label="Downloads" value={metricText(engagement?.DownloadClicks)} />
                <CompactStat label="Video plays" value={metricText(engagement?.VideoPlays)} />
            </div>
            <ScrollDepthFunnelTile bare funnel={engagement?.ScrollDepthFunnel} />
        </div>
    )
}

/** The three analytics views, surfaced as tabs so the card stays roughly one panel tall. */
type AnalyticsTab = 'funnel' | 'visitors' | 'engagement'
const ANALYTICS_TABS: { key: AnalyticsTab; label: string }[] = [
    { key: 'funnel', label: 'Funnel' },
    { key: 'visitors', label: 'Visitors' },
    { key: 'engagement', label: 'Engagement' },
]

/** Underline tab bar (matching the app's tab styling) for switching analytics views. */
function CardTabNav({ active, onChange }: { active: AnalyticsTab; onChange: (tab: AnalyticsTab) => void }) {
    return (
        <div className="border-b border-slate-200 dark:border-slate-700">
            <nav className="-mb-px flex gap-5" aria-label="Analytics views">
                {ANALYTICS_TABS.map(t => {
                    const isActive = active === t.key
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => onChange(t.key)}
                            aria-current={isActive ? 'page' : undefined}
                            className={
                                'whitespace-nowrap pb-2.5 text-sm font-medium border-b-2 transition-colors ' +
                                (isActive
                                    ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                                    : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-500')
                            }
                        >
                            {t.label}
                        </button>
                    )
                })}
            </nav>
        </div>
    )
}

/**
 * Web-level analytics card for the Landing Page tab. Owns the fetch + attribution state
 * and the loading/error states, delegating each UI region to a focused sub-component:
 * controls, the visitor funnel, and the range-less M1/M2 breakdowns. Measured (Builder)
 * pages surface the funnel, visitor, and engagement views as tabs so the card stays
 * compact next to the status card; external pages show the funnel only (visitor data
 * isn't measured off-platform).
 */
export function LandingPageAnalyticsCard({ workspaceId, webId, externalMode = false, canExport = false }: Props) {
    const [attributionView, setAttributionView] = useState<AttributionView>('ConversionSession')
    const [tab, setTab] = useState<AnalyticsTab>('funnel')
    const fetcher = useMemo(
        () => (workspaceId && webId ? () => websApi.getAnalytics(workspaceId, webId, false, attributionView) : null),
        [workspaceId, webId, attributionView],
    )
    const { data, loading, error, refresh } = useAnalyticsView(fetcher)

    // Only blank the panel on first load; on refresh keep the funnel visible (the icon spins).
    if (loading && !data) {
        return (
            <AnalyticsCardShell onRefresh={refresh} refreshing={loading}>
                <div className="flex items-center gap-2 py-4 text-sm text-slate-500 dark:text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading analytics…
                </div>
            </AnalyticsCardShell>
        )
    }

    if (error) {
        return (
            <AnalyticsCardShell onRefresh={refresh} refreshing={loading}>
                <div className="flex items-center gap-2 py-4 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            </AnalyticsCardShell>
        )
    }

    // External pages aren't measured beyond the funnel — no controls, no tabs.
    if (externalMode) {
        return (
            <AnalyticsCardShell onRefresh={refresh} refreshing={loading}>
                <LandingPageFunnel data={data} externalMode />
            </AnalyticsCardShell>
        )
    }

    return (
        <AnalyticsCardShell onRefresh={refresh} refreshing={loading}>
            <CardTabNav active={tab} onChange={setTab} />
            <div className="pt-5">
                {tab === 'funnel' && <LandingPageFunnel data={data} externalMode={false} />}
                {tab === 'visitors' && (
                    <div className="space-y-4">
                        <AnalyticsControls
                            workspaceId={workspaceId}
                            webId={webId}
                            attributionView={attributionView}
                            onAttributionChange={setAttributionView}
                            canExport={canExport}
                        />
                        <VisitorBreakdownSection data={data} />
                    </div>
                )}
                {tab === 'engagement' && <EngagementBreakdownSection data={data} />}
            </div>
        </AnalyticsCardShell>
    )
}
